//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { getRoutes, RouteObjectWithBreadcrumbs } from 'App';
import { ReactComponent as HomeIcon } from 'assets/home.svg';
import { useConfig } from 'contexts/ConfigProvider';
import { t } from 'i18next';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, matchRoutes, RouteMatch, useLocation, useParams } from 'react-router-dom';
interface DeviceHashTable {
  [key: string]: {
    name: string;
  };
}
interface RouteMatchWithBreadcrumb extends RouteMatch {
  route: RouteObjectWithBreadcrumbs;
}
const Breadcrumbs = () => {
  const { i18n } = useTranslation();
  const { buildVersion, allDevices = [], getDefaultConfig } = useConfig();
  const location = useLocation();
  const { serialNumber, name } = useParams();

  const createNewPath = (pathname: string) => {
    const pathNoSlash = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    if (pathNoSlash.includes('port-management') || pathNoSlash.includes('advanced-settings')) {
      let parts = pathNoSlash.split('/');
      // 移除最後一個path node，重新用 "/" 組合成字串
      parts.pop();
      return parts.join('/');
    }
    return pathNoSlash;
  };

  const isDisplayOnlyPath = (breadcrumbPath: string): boolean => {
    switch (breadcrumbPath) {
      case '/settings':
      case '/settings/profileManagement':
        return true;
      default:
        return false;
    }
  };

  const newPath = createNewPath(location.pathname);
  const routes = useMemo(
    () => getRoutes(buildVersion),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildVersion, i18n.language]
  );
  const matchedRoutes = useMemo(() => {
    const matched = matchRoutes(routes, newPath) as RouteMatchWithBreadcrumb[] | null;
    return matched?.filter((matchRoute) => matchRoute.pathnameBase === '/' || !!matchRoute.route.breadcrumb) ?? null;
  }, [routes, newPath]);

  const deviceHashTable = useMemo<DeviceHashTable>(() => {
    return allDevices.reduce<DeviceHashTable>((hashTable, device) => {
      hashTable[device.serialNumber] = { name: device.devNickname };
      return hashTable;
    }, {});
  }, [allDevices]);

  useEffect(() => {
    if (serialNumber && !deviceHashTable[serialNumber]) {
      getDefaultConfig();
    }
  }, [serialNumber, deviceHashTable, getDefaultConfig]);

  return (
    <nav>
      <ul className="flex items-center">
        {matchedRoutes &&
          matchedRoutes.map((matchRoute, i) => {
            const {
              pathnameBase,
              route: { breadcrumb },
            } = matchRoute;

            const isActive = pathnameBase.toLowerCase() === newPath.toLowerCase();

            const displayedBreadcrumb = (() => {
              if (breadcrumb === ':serialNumber') {
                if (serialNumber && deviceHashTable[serialNumber]) {
                  return deviceHashTable[serialNumber].name;
                }
                return serialNumber;
              }
              if (breadcrumb === ':name') {
                if (name === 'create' && location.search === '?new') {
                  return t('modal.btnCreateOne');
                }
                return name;
              }
              return breadcrumb;
            })();

            return isActive ? (
              <li key={i} className="text-cyfi-btn text-base">
                <span className="whitespace-pre-wrap">{displayedBreadcrumb}</span>
              </li>
            ) : pathnameBase === '/' ? (
              <li key={i} className="flex items-center">
                <Link to={pathnameBase} data-testid={`breadcrumb-link-to-${pathnameBase}`}>
                  <HomeIcon className="w-5 h-5" />
                </Link>
                {displayedBreadcrumb && pathnameBase && <span className="mx-3 text-cyfi-gray-550"> &gt; </span>}
                {/* &gt; == "<" */}
              </li>
            ) : isDisplayOnlyPath(pathnameBase) ? (
              <li key={i} className="text-cyfi-gray-550 text-sm font-normal">
                <span className="whitespace-pre-wrap">{displayedBreadcrumb}</span>
                {displayedBreadcrumb && pathnameBase && <span className="mx-3"> &gt; </span>}
                {/* &gt; == "<" */}
              </li>
            ) : (
              <li key={i} className="text-cyfi-gray-550 text-sm font-normal">
                {pathnameBase && (
                  <Link to={pathnameBase} className="whitespace-pre-wrap" data-testid={`breadcrumb-link-to-${pathnameBase}`}>
                    {displayedBreadcrumb}
                  </Link>
                )}
                {displayedBreadcrumb && pathnameBase && <span className="mx-3"> &gt; </span>}
                {/* &gt; == "<" */}
              </li>
            );
          })}
      </ul>
    </nav>
  );
};

export default Breadcrumbs;
